import type { InterrogationNodeV1, NpcInstanceV1 } from "@cold-case/shared/case-manifest";

import type { CoherenceJournal } from "./dialog-parser.js";
import { dialogConditionMet, substituteDialog } from "./dialog-parser.js";

export type NpcDialogFlowResult = {
  narrative: string;
  clueIdsToReveal: string[];
};

function appendClue(set: Set<string>, id: string | null | undefined): void {
  if (id?.trim()) set.add(id.trim());
}

export function npcConversationOpener(
  npc: NpcInstanceV1,
  names: string[],
  victimName: string
): string {
  const raw =
    String(npc.dialog.opener ?? "").trim() ||
    npc.dialog.idle_lines.find((s) => String(s).trim().length) ||
    "…";
  return substituteDialog(String(raw), names, victimName);
}

export function collectCluesForInterrogationNode(
  node: InterrogationNodeV1,
  met: boolean | null,
  into: Set<string>
): void {
  if (met === false) {
    return;
  }
  if (met === true && node.condition?.reveals_clue_if_met) {
    appendClue(into, node.condition.reveals_clue_if_met);
  }
  if (met === null || met === true) {
    appendClue(into, node.reveals_clue);
  }
}

export function answerForInterrogationNode(
  node: InterrogationNodeV1,
  journal: CoherenceJournal,
  playerCount: number
): { answer: string; met: boolean | null } {
  const c = node.condition;
  if (!c) {
    return { answer: node.response_default, met: null };
  }
  const met = dialogConditionMet(c, journal, playerCount);
  return {
    answer: met ? c.response_if_met : c.response_if_not_met,
    met,
  };
}

/**
 * Runs the full interrogation script for one NPC visit: every question in
 * manifest order, respecting `condition` branches and clue gates.
 */
export function runNpcInterrogationPass(
  npc: NpcInstanceV1,
  journal: CoherenceJournal,
  playerCount: number,
  names: string[],
  victimName: string
): NpcDialogFlowResult {
  const clueIds = new Set<string>();
  const lines: string[] = [];

  lines.push(npcConversationOpener(npc, names, victimName));

  for (const q of npc.dialog.interrogation ?? []) {
    const qText = substituteDialog(String(q.question_prompt ?? ""), names, victimName);
    const { answer, met } = answerForInterrogationNode(q, journal, playerCount);
    const aText = substituteDialog(String(answer ?? ""), names, victimName);
    lines.push(`\n— ${qText}\n${aText}`);

    collectCluesForInterrogationNode(q, met, clueIds);

    if (met === false && q.condition?.locked_hint?.trim()) {
      lines.push(`\n(${substituteDialog(q.condition.locked_hint.trim(), names, victimName)})`);
    }
  }

  const farewell = npc.dialog.farewell_lines?.find((s) => String(s).trim().length);
  if (farewell) {
    lines.push(`\n${substituteDialog(String(farewell), names, victimName)}`);
  }

  return {
    narrative: lines.join("").trim(),
    clueIdsToReveal: [...clueIds],
  };
}
